/* eslint-disable import/no-unresolved */
/* eslint-disable import/no-extraneous-dependencies */
import React from 'react'
import styled, { css } from 'styled-components'
import dayjs from 'dayjs'
import Datepicker from 'react-datepicker'

import 'react-datepicker/dist/react-datepicker.css'

// Phonenumber
import 'react-phone-number-input/style.css'
import PhoneInput from 'react-phone-number-input'
import en from 'react-phone-number-input/locale/en'
import nl from 'services/nl.json'

// Libraries
import { ErrorMessage } from 'formik'

// Elements
import { LabelCSS, InputCSS, TextareaCSS } from 'components/elements/Form'

// Components
import ParseContent from 'components/shared/ParseContent'
import { getLanguage } from 'services/language'

const StyledLabel = styled.label`
  ${LabelCSS};
`

const StyledInput = styled.input`
  ${InputCSS};
`

const StyledDatepicker = styled(Datepicker)`
  ${InputCSS};
`

const StyledTextArea = styled.textarea`
  ${TextareaCSS};

  height: 150px;
`

export const Error = styled(ErrorMessage)`
  background-color: ${(props) => props.theme.color.contrast};
  color: ${(props) => props.theme.color.text.light};
  font-size: ${(props) => props.theme.font.size.sm};
  margin: 0 auto;
  width: 50%;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  text-align: center;
`

const Description = styled.div`
  color: ${(props) => props.theme.color.text.main};
  font-size: ${(props) => props.theme.font.size.m};
  font-weight: ${(props) => props.theme.font.weight.s};
  margin-left: 15px;
`

const DescriptionProminent = styled.div`
  font-size: ${(props) => props.theme.font.size.m};
  font-weight: ${(props) => props.theme.font.weight.s};
  margin-left: 15px;
`

const OptionCSS = css`
  font-size: ${(props) => props.theme.font.size.m};
  font-weight: ${(props) => props.theme.font.weight.s};
  display: flex;
  align-items: center;
  justify-content: start;
  padding-right: 10px;

  & > span {
    text-align: left;
    text-decoration: ${(props) => (props.isActive ? 'underline' : 'none')};
    font-size: ${(props) => props.theme.font.size.sm};
  }
`

const Option = styled.div`
  ${OptionCSS};
  color: ${(props) => props.theme.color.text.main};
`

const OptionProminent = styled.div`
  ${OptionCSS};
  color: ${(props) => props.theme.color.text.secondary};
`

export const Checkbox = styled.div`
  border: 2px solid ${(props) => props.theme.color.background};
  min-height: 20px;
  min-width: 20px;
  max-height: 20px;
  max-width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`

export const Check = styled.div`
  display: inline-block;
  transform: rotate(45deg);
  height: 13px;
  width: 7px;
  border-bottom: 3px solid ${(props) => props.theme.color.secondary};
  border-right: 3px solid ${(props) => props.theme.color.secondary};
`

const PhoneWrapper = styled.div`
  & .PhoneInput {
    ${InputCSS};
    overflow: hidden;

    & input {
      border: none;
    }
  }
`

const StyledSelect = styled.select`
  ${InputCSS};
`

const Input = ({ edges, setData, data, setFieldValue }) => {
  let value = ''

  if (data[`input_${edges.id}`]) {
    value = data[`input_${edges.id}`]
  }

  if (edges.type === 'checkbox') {
    const options = JSON.parse(edges.choices)

    return (
      <div className="mt-5">
        <Description className="pt-3">
          <ParseContent content={edges.description} />
        </Description>
        <div className="mt-2">
          {options.map(({ text, value: optionValue }, index) => {
            const isActive = value === optionValue

            return (
              <OptionProminent
                // eslint-disable-next-line react/no-array-index-key
                key={`${edges.id}_${index}`}
                role="button"
                onClick={() => {
                  const event = {
                    target: {
                      name: `input_${edges.id}`,
                      value: isActive ? '' : optionValue,
                    },
                  }

                  setData(event)
                  setFieldValue(event.target.name, event.target.value)
                }}
                isActive={isActive}
              >
                <Checkbox className="mr-3">{isActive && <Check />}</Checkbox>
                <span>{text}</span>
              </OptionProminent>
            )
          })}
        </div>
        <Error
          name={`input_${edges.id}`}
          component="div"
          className="py-1 px-3"
        />
      </div>
    )
  }

  if (edges.type === 'multiselect') {
    const options = JSON.parse(edges.choices)

    return (
      <div className="mt-3">
        <DescriptionProminent className="pt-3">
          {edges.description}
        </DescriptionProminent>
        <div
          className="d-flex align-items-center justify-content-start mt-3"
          style={{ gap: 10, flexWrap: 'wrap' }}
        >
          {options.map(({ text, value: optionValue }, index) => {
            const isActive = value === optionValue

            return (
              <Option
                // eslint-disable-next-line react/no-array-index-key
                key={`${edges.id}_${index}`}
                role="button"
                onClick={() => {
                  const event = {
                    target: { name: `input_${edges.id}`, value: optionValue },
                  }

                  setData(event)
                  setFieldValue(event.target.name, event.target.value)
                }}
                isActive={isActive}
              >
                <Checkbox className="mr-2">{isActive && <Check />}</Checkbox>
                <span>{text}</span>
              </Option>
            )
          })}
        </div>
        <Error
          name={`input_${edges.id}`}
          component="div"
          className="py-1 px-3"
        />
      </div>
    )
  }

  if (edges.type === 'textarea') {
    return (
      <div className="mt-3">
        <StyledLabel htmlFor={`input_${edges.id}`}>
          {edges.label}
          {edges.isRequired && <span>*</span>}
        </StyledLabel>
        <StyledTextArea
          id={`input_${edges.id}`}
          value={value}
          type={edges.type}
          onChange={(event) => {
            setData(event)
            setFieldValue(event.target.name, event.target.value)
          }}
          name={`input_${edges.id}`}
        />
        <Error
          name={`input_${edges.id}`}
          component="div"
          className="py-1 px-3"
        />
      </div>
    )
  }

  if (edges.type === 'phone') {
    return (
      <div className="mt-3">
        {edges.description && (
          <Description className="pt-3">{edges.description}</Description>
        )}

        {!edges.description && (
          <StyledLabel htmlFor={`input_${edges.id}`}>
            {edges.label}
            {edges.isRequired && <span>*</span>}
          </StyledLabel>
        )}
        <PhoneWrapper>
          <PhoneInput
            labels={getLanguage() === 'nl_NL' ? nl : en}
            id={`input_${edges.id}`}
            name={`input_${edges.id}`}
            placeholder={edges.placeholder}
            value={value}
            onChange={(number) => {
              const event = {
                target: {
                  value: number,
                  type: edges.type,
                  name: `input_${edges.id}`,
                },
              }

              setData(event)
              setFieldValue(event.target.name, event.target.value)
            }}
          />
        </PhoneWrapper>
        <Error
          name={`input_${edges.id}`}
          component="div"
          className="py-1 px-3"
        />
      </div>
    )
  }

  if (edges.type === 'date') {
    const minDate = dayjs().add(1, 'day').toDate()
    return (
      <div className="mt-3">
        {edges.description && (
          <Description className="pt-3">{edges.description}</Description>
        )}

        {!edges.description && (
          <StyledLabel htmlFor={`input_${edges.id}`}>
            {edges.label}
            {edges.isRequired && <span>*</span>}
          </StyledLabel>
        )}

        <StyledDatepicker
          minDate={minDate}
          dateFormat="dd/MM/yyyy"
          selected={dayjs(value).isValid() ? dayjs(value).toDate() : null}
          onChange={(newValue) => {
            if (!newValue) {
              const event = {
                target: {
                  value: null,
                  type: edges.type,
                  name: `input_${edges.id}`,
                },
              }
              setData(event)
              setFieldValue(`input_${edges.id}`, null)
              return
            }

            const date = dayjs(newValue).format('YYYY-MM-DD')
            const event = {
              target: {
                value: date,
                type: edges.type,
                name: `input_${edges.id}`,
              },
            }

            setData(event)
            setFieldValue(`input_${edges.id}`, date)
          }}
          placeholderText="dd-mm-yyyy"
          onChangeRaw={(event) => {
            event.preventDefault()
          }}
        />

        <Error
          name={`input_${edges.id}`}
          component="div"
          className="py-1 px-3"
        />
      </div>
    )
  }

  if (edges.type === 'select') {
    const options = JSON.parse(edges.choices)

    return (
      <div className="mt-3">
        {edges.description && (
        <Description className="pt-3">{edges.description}</Description>
      )}

      {!edges.description && (
        <StyledLabel htmlFor={`input_${edges.id}`}>
          {edges.label}
          {edges.isRequired && <span>*</span>}
        </StyledLabel>
      )}
        <StyledSelect
          className="d-flex align-items-center justify-content-start mt-3"
          style={{ gap: 10, flexWrap: 'wrap' }}
          placeholder='Selecteer een land'
          defaultValue=""
          onChange={(e) => {
            const event = {
              target: { name: `input_${edges.id}`, value: e.target.value },
            }

            setData(event)
            setFieldValue(event.target.name, event.target.value)
          }}
        >
          <option disabled value="">Selecteer een land</option>
          {options.map((option, index) => (
              <option
                // eslint-disable-next-line react/no-array-index-key
                key={`${edges.id}_${index}`}
                role="button"
                
                value={option.value}
              >
                {option.text}
              </option>
            )
          )}
        </StyledSelect>
        <Error
          name={`input_${edges.id}`}
          component="div"
          className="py-1 px-3"
        />
      </div>
    )
  }

  return (
    <div className="mt-3">
      {edges.description && (
        <Description className="pt-3">{edges.description}</Description>
      )}

      {!edges.description && (
        <StyledLabel htmlFor={`input_${edges.id}`}>
          {edges.label}
          {edges.isRequired && <span>*</span>}
        </StyledLabel>
      )}
      <StyledInput
        id={`input_${edges.id}`}
        value={value}
        type={edges.type}
        onChange={(event) => {
          setData(event)
          setFieldValue(event.target.name, event.target.value)
        }}
        name={`input_${edges.id}`}
        placeholder={edges.placeholder}
      />
      <Error name={`input_${edges.id}`} component="div" className="py-1 px-3" />
    </div>
  )
}

export default Input
